/**
 * Closing or reloading the Properties panel with unsaved edits (UI/UX fix round A, review A2): Save, Discard or Cancel
 * — the Table Explorer's unsaved-changes treatment. Save is offered only when every edited section can be saved; its
 * reason shows otherwise — in the dialog body, not only on the button (final UI/UX batch C1, R8 / R9).
 * While a write is in flight (R1) Discard waits for it (it cannot recall a write already sent); Cancel stops Save after
 * that write and the Close / Reload it was asked for never runs.
 */
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import { Button } from '../../../../components/ui/button';
import { ReasonButton } from '../../../../components/ui/ReasonButton';

export type PropertiesGuardAction = 'close' | 'reload';

export function PropertiesUnsavedDialog(props: {
  action: PropertiesGuardAction | null;
  sections: string[];
  saveReason?: string;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}) {
  const verb = props.action === 'reload' ? 'reloading' : 'closing';
  return (
    <AlertDialog open={props.action !== null} onOpenChange={(open) => { if (!open) props.onCancel(); }}>
      <AlertDialogContent data-testid="storage-properties-unsaved-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-warning" aria-hidden="true" />Unsaved changes</AlertDialogTitle>
          <AlertDialogDescription>
            {props.sections.join(', ')} {props.sections.length === 1 ? 'has' : 'have'} unsaved changes. Save or discard them before {verb} the properties.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {props.saveReason && !props.saving && <p className="rounded-md border border-warning/40 bg-warning/10 p-2 text-sm" data-testid="storage-properties-unsaved-reason">{props.saveReason}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="storage-properties-unsaved-cancel">Cancel</AlertDialogCancel>
          <Button type="button" variant="outline" disabled={props.saving} onClick={props.onDiscard} data-testid="storage-properties-unsaved-discard">Discard</Button>
          <ReasonButton type="button" reason={props.saveReason} disabled={props.saving} onClick={props.onSave} data-testid="storage-properties-unsaved-save">{props.saving ? 'Saving…' : 'Save'}</ReasonButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
